import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  CircularProgress,
  Divider,
  Badge,
  Slider
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '@mui/material/styles';
import { POKEMON_TYPES, getTypeColor } from '../../utils/pokemonTypes';
import { formatGameFormat } from '../../utils/formatHelpers';

const STAT_CONFIG = [
  { key: 'hp',              label: 'HP',      max: 255, color: '#FF5959' },
  { key: 'attack',          label: 'Atk',     max: 255, color: '#F5AC78' },
  { key: 'defense',         label: 'Def',     max: 255, color: '#FAE078' },
  { key: 'special-attack',  label: 'Sp.Atk',  max: 255, color: '#9DB7F5' },
  { key: 'special-defense', label: 'Sp.Def',  max: 255, color: '#A7DB8D' },
  { key: 'speed',           label: 'Spe',     max: 255, color: '#FA92B2' },
  { key: 'total',           label: 'Total',   max: 780, color: '#B8B8D0' },
];

const DEFAULT_STAT_FILTERS = Object.fromEntries(STAT_CONFIG.map(s => [s.key, 0]));

/**
 * PokemonFilters - Unified filter panel combining format, type and stat filters
 */
const PokemonFilters = ({
  // Format filter
  selectedFormat,
  onFormatChange,
  formats,
  formatsLoading,
  // Type filter
  selectedTypes,
  onTypesChange,
  filterMode,
  onFilterModeChange,
  // Stat filters
  statFilters,
  onStatFiltersChange,
  // Stats
  filteredCount,
  totalCount,
  // Panel state
  expanded,
  onExpandedChange
}) => {
  const theme = useTheme();

  const activeStatCount = Object.values(statFilters || {}).filter(v => v > 0).length;
  const activeCount = (selectedFormat ? 1 : 0) + selectedTypes.length + activeStatCount;

  // Format autocomplete options grouped by generation
  const formatOptions = (formats || []).map(fmt => ({
    id: fmt.id,
    label: formatGameFormat(fmt.id),
    group: (() => {
      const gen = fmt.id.match(/gen(\d+)/)?.[1];
      return gen ? `Generation ${gen}` : 'Other';
    })()
  }));

  const selectedFormatOption = selectedFormat
    ? (formatOptions.find(o => o.id === selectedFormat) || { id: selectedFormat, label: formatGameFormat(selectedFormat), group: '' })
    : null;

  const handleTypeClick = (type) => {
    if (selectedTypes.includes(type)) {
      onTypesChange(selectedTypes.filter(t => t !== type));
    } else {
      onTypesChange([...selectedTypes, type]);
    }
  };

  const handleClearAll = () => {
    onFormatChange(null);
    onTypesChange([]);
    onStatFiltersChange(DEFAULT_STAT_FILTERS);
  };

  const handleStatChange = (statKey, value) => {
    onStatFiltersChange({ ...statFilters, [statKey]: value });
  };

  const handleClearStats = () => {
    onStatFiltersChange(DEFAULT_STAT_FILTERS);
  };

  const inputSx = {
    color: 'white',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 3,
        backgroundColor: 'rgba(30, 30, 30, 0.9)',
        border: activeCount > 0
          ? `2px solid ${theme.palette.primary.main}`
          : '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'border-color 0.3s ease',
        overflow: 'hidden'
      }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        onClick={() => onExpandedChange(!expanded)}
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' }
        }}
      >
        <TuneIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />

        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', flexShrink: 0 }}>
          Filters
        </Typography>

        {activeCount > 0 && (
          <Badge
            badgeContent={activeCount}
            sx={{
              flexShrink: 0,
              '& .MuiBadge-badge': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                fontWeight: 'bold'
              }
            }}
          >
            <Box sx={{ width: 8 }} />
          </Badge>
        )}

        {/* Active filter chips preview (collapsed only) */}
        {!expanded && activeCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {selectedFormat && (
              <Chip
                label={formatGameFormat(selectedFormat)}
                size="small"
                onDelete={(e) => { e.stopPropagation(); onFormatChange(null); }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  backgroundColor: `${theme.palette.primary.main}33`,
                  color: 'white',
                  border: `1px solid ${theme.palette.primary.main}55`,
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: 22,
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '14px',
                    '&:hover': { color: theme.palette.error.main }
                  }
                }}
              />
            )}
            {selectedTypes.map(type => (
              <Tooltip key={type} title={type}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: getTypeColor(type),
                    border: '2px solid rgba(255,255,255,0.25)',
                    flexShrink: 0,
                    cursor: 'pointer'
                  }}
                  onClick={(e) => { e.stopPropagation(); handleTypeClick(type); }}
                />
              </Tooltip>
            ))}
          </Box>
        )}

        {/* Right side: count + clear + toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', flexShrink: 0 }}>
          {activeCount > 0 && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mr: 0.5 }}>
              {filteredCount}/{totalCount}
            </Typography>
          )}
          {activeCount > 0 && (
            <Tooltip title="Clear all filters">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  '&:hover': { color: theme.palette.error.main, backgroundColor: 'rgba(244,67,54,0.1)' }
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            sx={{ color: 'rgba(255,255,255,0.5)', pointerEvents: 'none' }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* ── Expanded content ────────────────────────────────────── */}
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Format section */}
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', letterSpacing: '0.1em', display: 'block', mb: 1 }}
            >
              VGC Format
            </Typography>
            <Autocomplete
              value={selectedFormatOption}
              onChange={(_, newValue) => onFormatChange(newValue?.id || null)}
              options={formatOptions}
              groupBy={(option) => option.group}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              loading={formatsLoading}
              loadingText="Loading formats..."
              noOptionsText="No formats found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={formatsLoading ? 'Loading formats...' : 'All formats (no filter)'}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {formatsLoading && (
                          <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.5 }} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    sx: inputSx
                  }}
                  inputProps={{ ...params.inputProps, style: { color: 'white' } }}
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box sx={{ px: 2, py: 0.75, backgroundColor: 'rgba(34,31,199,0.2)', color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    {params.group}
                  </Box>
                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                </li>
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id} style={{ ...props.style, color: 'white', fontSize: '0.875rem' }}>
                  {option.label}
                </li>
              )}
              sx={{
                '& .MuiAutocomplete-popupIndicator': { color: 'rgba(255,255,255,0.4)' },
                '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255,255,255,0.4)' }
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: '#1e1e1e',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '& .MuiAutocomplete-option': {
                      color: 'white',
                      '&[aria-selected="true"]': { backgroundColor: 'rgba(34,31,199,0.35) !important' },
                      '&.Mui-focused': { backgroundColor: 'rgba(255,255,255,0.06)' }
                    }
                  }
                }
              }}
            />
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

          {/* Types section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', letterSpacing: '0.1em' }}
              >
                Type
              </Typography>

              {selectedTypes.length > 1 && (
                <ToggleButtonGroup
                  value={filterMode}
                  exclusive
                  onChange={(_, v) => v && onFilterModeChange(v)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: 'rgba(255,255,255,0.5)',
                      borderColor: 'rgba(255,255,255,0.15)',
                      py: 0.25,
                      px: 1,
                      fontSize: '0.7rem',
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': { backgroundColor: theme.palette.primary.dark }
                      }
                    }
                  }}
                >
                  <ToggleButton value="OR">
                    <Tooltip title="Pokémon with ANY of the selected types">
                      <span>Any (OR)</span>
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="AND">
                    <Tooltip title="Pokémon with ALL selected types">
                      <span>All (AND)</span>
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}

              {selectedTypes.length > 0 && (
                <Tooltip title="Clear type filter">
                  <IconButton
                    size="small"
                    onClick={() => onTypesChange([])}
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      '&:hover': { color: theme.palette.error.main, backgroundColor: 'rgba(244,67,54,0.1)' }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {POKEMON_TYPES.map((type) => {
                const isSelected = selectedTypes.includes(type);
                return (
                  <Chip
                    key={type}
                    label={type}
                    onClick={() => handleTypeClick(type)}
                    sx={{
                      backgroundColor: isSelected ? getTypeColor(type) : 'rgba(255,255,255,0.08)',
                      color: 'white',
                      fontWeight: isSelected ? 'bold' : 'normal',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      border: isSelected ? `2px solid ${getTypeColor(type)}` : '2px solid transparent',
                      filter: isSelected ? 'brightness(1.1)' : 'brightness(0.75)',
                      transition: 'all 0.15s ease',
                      height: 28,
                      '&:hover': {
                        filter: 'brightness(1)',
                        transform: 'scale(1.05)',
                        boxShadow: `0 0 8px ${getTypeColor(type)}55`
                      },
                      '& .MuiChip-label': { px: 1.25 }
                    }}
                  />
                );
              })}
            </Box>

            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 1.5, textAlign: 'center' }}
            >
              Click types to filter · Select multiple to find dual-type Pokémon
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

          {/* Stats section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography
                variant="overline"
                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', letterSpacing: '0.1em' }}
              >
                Base Stats (minimum)
              </Typography>
              {activeStatCount > 0 && (
                <Tooltip title="Clear stat filters">
                  <IconButton
                    size="small"
                    onClick={handleClearStats}
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      '&:hover': { color: theme.palette.error.main, backgroundColor: 'rgba(244,67,54,0.1)' }
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {STAT_CONFIG.map(({ key, label, max, color }) => {
                const value = statFilters?.[key] ?? 0;
                const isActive = value > 0;
                return (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isActive ? color : 'rgba(255,255,255,0.4)',
                        fontWeight: isActive ? 'bold' : 'normal',
                        width: 44,
                        flexShrink: 0,
                        fontSize: '0.72rem',
                        transition: 'color 0.15s ease'
                      }}
                    >
                      {label}
                    </Typography>
                    <Slider
                      value={value}
                      min={0}
                      max={max}
                      step={1}
                      onChange={(_, v) => handleStatChange(key, v)}
                      size="small"
                      sx={{
                        flex: 1,
                        color: isActive ? color : 'rgba(255,255,255,0.2)',
                        '& .MuiSlider-thumb': {
                          width: 14,
                          height: 14,
                          backgroundColor: isActive ? color : 'rgba(255,255,255,0.4)',
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0 0 0 6px ${color}33`
                          }
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255,255,255,0.12)',
                          opacity: 1
                        },
                        '& .MuiSlider-track': {
                          backgroundColor: isActive ? color : 'rgba(255,255,255,0.2)',
                          border: 'none'
                        }
                      }}
                    />
                    <TextField
                      value={value === 0 ? '' : value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') { handleStatChange(key, 0); return; }
                        const num = parseInt(raw, 10);
                        if (!isNaN(num)) handleStatChange(key, Math.min(max, Math.max(0, num)));
                      }}
                      onBlur={(e) => {
                        if (e.target.value === '') handleStatChange(key, 0);
                      }}
                      placeholder="0"
                      size="small"
                      inputProps={{ min: 0, max, style: { textAlign: 'center', color: isActive ? 'white' : 'rgba(255,255,255,0.4)', padding: '2px 4px', fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums', MozAppearance: 'textfield' } }}
                      sx={{
                        width: 46,
                        flexShrink: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: isActive ? `${color}18` : 'transparent',
                          transition: 'background-color 0.15s ease',
                          '& fieldset': { borderColor: isActive ? `${color}88` : 'rgba(255,255,255,0.12)', transition: 'border-color 0.15s ease' },
                          '&:hover fieldset': { borderColor: isActive ? color : 'rgba(255,255,255,0.3)' },
                          '&.Mui-focused fieldset': { borderColor: color },
                        },
                        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none' },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 1.5, textAlign: 'center' }}
            >
              Drag sliders or type a value · Shows Pokémon with stats ≥ the threshold
            </Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PokemonFilters;
